import React from "react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { BookOpenCheck, FileWarning, Scale, ShieldCheck, Layers, Flag } from "lucide-react";

const Principle = ({ icon: Icon, title, children }) => (
    <div className="bg-[#111827] border border-[#243044] rounded-xl p-6">
        <div className="w-10 h-10 rounded-lg bg-[#2F80ED]/15 text-[#56CCF2] flex items-center justify-center mb-4">
            <Icon className="w-5 h-5" />
        </div>
        <h3 className="text-base font-bold text-white mb-2">{title}</h3>
        <p className="text-sm text-[#94A3B8] leading-relaxed">{children}</p>
    </div>
);

export default function MethodologyPage() {
    return (
        <div className="min-h-screen bg-[#0B1220]">
            <Header />
            <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
                <div className="text-[10px] uppercase tracking-[0.25em] text-[#56CCF2] font-medium mb-4">Methodology</div>
                <h1 className="text-4xl sm:text-5xl font-bold text-white tracking-tight mb-6">How we work</h1>
                <p className="text-lg text-[#94A3B8] leading-relaxed max-w-3xl mb-12">
                    The Context Project is a structured investigative library. Every topic follows the same rigorous framework so that
                    readers can interrogate the evidence themselves rather than be told what to think.
                </p>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-16">
                    <Principle icon={Layers} title="13-section structure">
                        Every topic follows the same template — hero, why-it-matters, official position, system breakdown,
                        critical questions, tiered evidence, both sides, examples, unknowns, future, takeaways. Consistency
                        is what allows comparison.
                    </Principle>
                    <Principle icon={BookOpenCheck} title="Evidence tiered">
                        Evidence is split into <span className="text-white font-medium">documented</span> (verifiable in primary sources),
                        <span className="text-white font-medium"> reported</span> (credible secondary reporting), and
                        <span className="text-white font-medium"> debated</span> (contested or speculative). Readers see the difference.
                    </Principle>
                    <Principle icon={ShieldCheck} title="Sources labelled">
                        Every source carries a funding label — independent, industry-funded, government, or unknown. We do not
                        dismiss any source by default. We surface the conflict and let you weigh it.
                    </Principle>
                    <Principle icon={Scale} title="Both sides represented">
                        Every topic carries a supporting view and a critical view. Where they conflict, we say so. Where consensus exists,
                        we name it. We don't strawman either side.
                    </Principle>
                    <Principle icon={FileWarning} title="What's unclear is named">
                        Every investigation explicitly lists what is unresolved, contested, or unknown. Confidence and concern scores
                        reflect this — they are not opinions; they are the strength of the documented record.
                    </Principle>
                    <Principle icon={Flag} title="Errors get fixed">
                        Every topic carries a "Report an issue" link. Corrections are reviewed and applied. The library is
                        versioned — we treat it like a working document, not a finished verdict.
                    </Principle>
                </div>

                <div className="bg-[#111827] border border-[#243044] rounded-2xl p-8 space-y-5">
                    <h2 className="text-2xl font-bold text-white">What we are not</h2>
                    <ul className="space-y-3 text-sm text-[#CBD5E1] leading-relaxed">
                        <li>· We are <span className="text-white font-medium">not</span> a news site. Topics are slow, structured, and updated as evidence changes.</li>
                        <li>· We are <span className="text-white font-medium">not</span> a debunking site. We don't tell you who's right. We show you the record.</li>
                        <li>· We are <span className="text-white font-medium">not</span> partisan. Both supporting and critical perspectives appear on every topic — by structure, not opinion.</li>
                        <li>· We are <span className="text-white font-medium">not</span> in a hurry. Topics are released when the evidence is structured, not when the news cycle demands.</li>
                    </ul>
                </div>

                <div className="mt-12 pt-8 border-t border-[#243044] text-sm text-[#94A3B8]">
                    <p className="mb-2"><span className="text-white font-medium">Compiled by:</span> Bill Mathers — The Context Project</p>
                    <p>Questions or corrections: <a className="text-[#56CCF2] hover:text-white" href="https://thecontextproject.com/pages/report-an-issue" target="_blank" rel="noreferrer">report an issue</a>.</p>
                </div>
            </section>
            <Footer />
        </div>
    );
}
